import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Button,
} from 'reactstrap';
import classnames from 'classnames';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { getPropertyById } from '../../services/propertyService';
import { getLandById } from 'services/lands';
import {
  deleteSubProperty,
  getSubPropertyById,
} from 'services/subPropertyService';
import {
  getTenantBySubPropertyId,
  deleteTenant,
  disableTenant,
  getDisabledTenants,
} from '../../services/tenantService';
import { formatFloorLabel, getApiErrorMessage } from 'utility/utilityFunctions';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useProfile } from 'Components/Hooks/UserHooks';
import moment from 'moment';
import TenantModel from '../Properties/tenant-model';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import { handleViewFile } from 'utility/utilityFunctions';
import { Property } from 'utility/interface';
import { getHistoryBySubPropertyId } from 'services/subPropertyService';
import SubPropertyHistoryModal from './history-modal';
import SubPropertyModal from 'pages/Properties/sub-property-modal';
import ConfirmDialog from 'Components/Common/ConfirmDialog';
import { getNavigationBreadcrumbs } from 'utility/breadcrumbUtils';
import { getFileViewUrl } from 'services/fileService';

const SubProperties = () => {
  const { propertyId, id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useProfile();
  const [editModal, setEditModal] = useState(false);
  const [property, setProperty] = useState<Property>(null);
  const [subProperty, setSubProperty] = useState<Property>(null);
  const [land, setLand] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTenantModal, setDeleteTenantModal] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [activeTab, setActiveTab] = useState('1');
  const [tenantModal, setTenantModal] = useState(false);
  const [histories, setHistories] = useState<any[]>([]);
  const [historyModal, setHistoryModal] = useState(false);
  const [disableConfirm, setDisableConfirm] = useState(false);
  const [disabledTenants, setDisabledTenants] = useState<any[]>([]);
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(window.location.search);
  const mazarId = searchParams.get('mazar_id');
  const mazarName = searchParams.get('mazar_name');
  const propertyName = searchParams.get('property_name');
  const trustRegNumber = searchParams.get('trustRegNumber');
  const trustRegId = searchParams.get('trustRegId');
  const surveyNumber = searchParams.get('surveyNumber') || land?.surveyNumber;
  const landId = searchParams.get('landId');
  const category = searchParams.get('category');

  const breadCrumbItems = [
    ...getNavigationBreadcrumbs({
      organizationId: mazarId || '',
      mazarName,
      category,
      trustRegNumber,
      trustRegId,
      surveyNumber,
      landId,
      includeSurvey: true,
    }),

    {
      label: propertyName || '-',
      path: `/properties/${propertyId}?mazar_name=${mazarName}&property_name=${propertyName}&mazar_id=${mazarId}&trustRegId=${trustRegId}&surveyNumber=${surveyNumber}&landId=${landId}&category=${category}&trustRegNumber=${trustRegNumber}`,
    },

    {
      label: subProperty?.name || '-',
      path: `/properties/${propertyId}/sub-properties/${id}?mazar_name=${mazarName}&property_name=${propertyName}&mazar_id=${mazarId}&trustRegId=${trustRegId}&surveyNumber=${surveyNumber}&landId=${landId}&category=${category}&trustRegNumber=${trustRegNumber}`,
      isActive: true,
    },
  ];

  const docs = [
    {
      label: 'House Tax Bill',
      value: subProperty?.houseTaxBill
        ? subProperty.houseTaxBill.split(',')
        : [],
      icon: 'ri-home-gear-line',
    },
    {
      label: 'Jantri Document',
      value: subProperty?.jantriDocument
        ? subProperty.jantriDocument.split(',')
        : [],
      icon: 'ri-file-text-line',
    },
    {
      label: 'Case Document',
      value: subProperty?.caseDocument
        ? subProperty.caseDocument.split(',')
        : [],
      icon: 'ri-file-text-line',
    },
    {
      label: 'Commercial Properties List',
      value: subProperty?.commercialPropertiesList
        ? subProperty.commercialPropertiesList.split(',')
        : [],
      icon: 'ri-building-line',
    },
    {
      label: 'Residential Properties List',
      value: subProperty?.residentialPropertiesList
        ? subProperty.residentialPropertiesList.split(',')
        : [],
      icon: 'ri-home-4-line',
    },
    {
      label: 'Agricultural Lands on Rent',
      value: subProperty?.agriculturalLandsOnRent
        ? subProperty.agriculturalLandsOnRent.split(',')
        : [],
      icon: 'ri-plant-line',
    },
    {
      label: 'Tenant Dispute List',
      value: subProperty?.tenantDisputeList
        ? subProperty.tenantDisputeList.split(',')
        : [],
      icon: 'ri-file-warning-line',
    },
    {
      label: 'Litigation Copies',
      value: subProperty?.litigationCopies
        ? subProperty.litigationCopies.split(',')
        : [],
      icon: 'ri-file-shield-line',
    },
    {
      label: 'Rent Receipts',
      value: subProperty?.rentReceipts
        ? subProperty.rentReceipts.split(',')
        : [],
      icon: 'ri-home-gear-line',
    },
    {
      label: 'Water Bills',
      value: subProperty?.waterBills ? subProperty.waterBills.split(',') : [],
      icon: 'ri-water-flash-line',
    },
    {
      label: 'Property Tax Bills',
      value: subProperty?.propertyTaxBills
        ? subProperty.propertyTaxBills.split(',')
        : [],
      icon: 'ri-money-dollar-circle-line',
    },
    {
      label: 'Gas Connection Bills',
      value: subProperty?.gasConnectionBills
        ? subProperty.gasConnectionBills.split(',')
        : [],
      icon: 'ri-fire-line',
    },
    {
      label: 'Digital Records of Bills',
      value: subProperty?.digitalRecordsOfBills
        ? subProperty.digitalRecordsOfBills.split(',')
        : [],
      icon: 'ri-folder-chart-line',
    },
    {
      label: 'List of Cases',
      value: subProperty?.listOfCases ? subProperty.listOfCases.split(',') : [],
      icon: 'ri-list-check-2',
    },
    {
      label: 'Case Files',
      value: subProperty?.caseFiles ? subProperty.caseFiles.split(',') : [],
      icon: 'ri-folder-line',
    },
    {
      label: 'Interim/Final Orders',
      value: subProperty?.interimFinalOrders
        ? subProperty.interimFinalOrders.split(',')
        : [],
      icon: 'ri-file-list-3-line',
    },
    {
      label: 'Judgment Copies',
      value: subProperty?.judgmentCopies
        ? subProperty.judgmentCopies.split(',')
        : [],
      icon: 'ri-article-line',
    },
    {
      label: 'Appeals Filed',
      value: subProperty?.appealsFiled
        ? subProperty.appealsFiled.split(',')
        : [],
      icon: 'ri-file-upload-line',
    },
    {
      label: 'Closed Cases',
      value: subProperty?.closedCases ? subProperty.closedCases.split(',') : [],
      icon: 'ri-checkbox-circle-line',
    },
    {
      label: 'Ongoing Cases',
      value: subProperty?.ongoingCases
        ? subProperty.ongoingCases.split(',')
        : [],
      icon: 'ri-time-line',
    },
    {
      label: 'Advocate Details',
      value: subProperty?.advocateDetails
        ? subProperty.advocateDetails.split(',')
        : [],
      icon: 'ri-user-star-line',
    },
  ];

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
  };

  const onClickDelete = () => {
    setDeleteModal(true);
  };

  const toggleTenantModal = () => {
    setTenantModal(!tenantModal);
  };

  const loadPropertyDetails = async () => {
    dispatch(isVisible(true));
    try {
      const result = await getPropertyById(propertyId || '');
      if (result?.data) {
        setProperty(result.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const loadSubPropertyDetails = async () => {
    dispatch(isVisible(true));
    try {
      const result = await getSubPropertyById(id || '');
      if (result?.data) {
        setSubProperty(result.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message, {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const loadLandDetails = async () => {
    if (!landId) return;
    try {
      const result = await getLandById(landId);
      if (result?.data) {
        setLand(result.data);
      }
    } catch (err) {
      console.error('Error fetching land details:', err);
    }
  };

  const loadTenantDetails = async () => {
    dispatch(isVisible(true));
    try {
      const result = await getTenantBySubPropertyId(id || '');
      if (result?.data) {
        setTenant(result.data);
      }
    } catch (err) {
      // Don't show error if no tenant exists
      if (err?.response?.status !== 404) {
        toast.error(err?.response?.data?.message || err?.message, {
          autoClose: 2000,
        });
      }
    } finally {
      dispatch(isVisible(false));
    }
  };

  const loadDisabledTenants = async () => {
    try {
      const res = await getDisabledTenants(id || '');

      if (res?.data) setDisabledTenants(res.data);
    } catch (err: any) {
      console.error('Error in loadDisabledTenants', err);
      toast.error(`Error in loadDisabledTenants: ${err}`);
    }
  };

  const loadHistories = async () => {
    dispatch(isVisible(true));
    try {
      const res = await getHistoryBySubPropertyId(id || '');
      if (res?.data) setHistories(res.data);
    } catch (err: any) {
      console.error('Error in load histories', err);
    } finally {
      dispatch(isVisible(false));
    }
  };

  const inactivateTenant = async () => {
    dispatch(isVisible(true));
    try {
      await disableTenant(userProfile.id, tenant.id);
      toast.success('Tenant marked inactive');
      setTenant(null);
      setTenantModal(true);
      loadDisabledTenants();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      dispatch(isVisible(false));
      setDisableConfirm(false);
    }
  };

  const handleDeleteSubProperty = async () => {
    dispatch(isVisible(true));
    try {
      await deleteSubProperty(id || '');
      toast.success('Sub property deleted successfully', { autoClose: 2000 });
      setDeleteModal(false);
      navigate(
        `/properties/${propertyId}?mazar_name=${mazarName}&mazar_id=${mazarId}&property_name=${encodeURIComponent(propertyName)}`,
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Sub property delete failed'), {
        autoClose: 2000,
      });
    } finally {
      dispatch(isVisible(false));
    }
  };

  const handleDeleteTenant = async () => {
    if (!tenant) return;

    dispatch(isVisible(true));
    try {
      await deleteTenant(tenant.id);
      toast.success('Tenant deleted');
      setTenant(null);
      loadDisabledTenants();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      dispatch(isVisible(false));
    }
  };

  useEffect(() => {
    loadPropertyDetails();
    loadSubPropertyDetails();
    loadTenantDetails();
    loadDisabledTenants();
    loadHistories();
    loadLandDetails();
  }, [id, landId]);

  if (!property || !subProperty) {
    return <div>Loading...</div>;
  }

  return (
    <React.Fragment>
      <div
        className="page-content"
        style={{ maxHeight: 'calc(100vh - 20px)', overflowY: 'auto' }}
      >
        <ToastContainer closeButton={false} limit={1} />
        <ConfirmDialog
          isOpen={deleteModal}
          title="Confirm Delete"
          message="Are you sure you want to delete this sub property?"
          confirmLabel="Yes, Delete"
          confirmBtnColor="danger"
          iconClass="ri-delete-bin-line text-danger"
          onConfirm={handleDeleteSubProperty}
          onCancel={() => setDeleteModal(false)}
        />
        <ConfirmDialog
          isOpen={disableConfirm}
          title="Mark Tenant as Inactive"
          message="Are you sure you want to mark this tenant as inactive?"
          confirmLabel="Yes"
          confirmBtnColor="danger"
          iconClass="ri-delete-bin-line text-danger"
          onConfirm={inactivateTenant}
          onCancel={() => setDisableConfirm(false)}
        />
        <ConfirmDialog
          isOpen={deleteTenantModal}
          title="Confirm Delete"
          message="Are you sure you want to delete this tenant?"
          confirmLabel="Yes"
          confirmBtnColor="danger"
          iconClass="ri-delete-bin-line text-danger"
          onConfirm={handleDeleteTenant}
          onCancel={() => setDeleteTenantModal(false)}
        />
        <Container fluid>
          <BreadCrumb
            title="Sub Property Details"
            pageTitle="Sub Properties"
            breadcrumbItems={breadCrumbItems}
          />
          {/* Details Card */}
          <Row>
            <Col lg={12}>
              <Card className="overflow-hidden">
                <CardBody>
                  {/* Header & quick stats */}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-0">
                      {subProperty?.name || 'Sub Property'}
                    </h4>
                    <div className="d-flex gap-2">
                      <Button color="primary" onClick={toggleEditModal}>
                        <i className="ri-edit-line align-bottom me-1"></i> Edit
                        Sub Property
                      </Button>
                      <Button color="danger" onClick={onClickDelete}>
                        <i className="ri-delete-bin-line align-bottom me-1"></i>{' '}
                        Delete Sub Property
                      </Button>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <Row className="mb-4 g-3">
                    {[
                      {
                        label: 'Floor',
                        value:
                          formatFloorLabel(subProperty.floor) ||
                          subProperty.customFloor ||
                          '-',
                      },
                      {
                        label: 'Rent Amount (₹)',
                        value: subProperty.rentAmount ?? '-',
                      },
                      {
                        label: 'Tenant',
                        value: tenant ? tenant.tenantName : 'No Tenant',
                      },
                      { label: 'Size (sq.ft)', value: subProperty.size ?? '-' },
                    ].map((stat, idx) => (
                      <Col lg={3} md={6} key={idx}>
                        <Card className="shadow-sm border-0 h-100 bg-light-subtle overflow-hidden">
                          <CardBody className="text-center p-3">
                            <h2 className="mb-0 fw-semibold text-primary">
                              {stat.value}
                            </h2>
                            <p className="text-muted mb-0 small text-uppercase fw-medium">
                              {stat.label}
                            </p>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  <Nav tabs className="nav-tabs-custom nav-justified">
                    <NavItem>
                      <NavLink
                        style={{ cursor: 'pointer' }}
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => toggleTab('1')}
                      >
                        <span className="d-block d-sm-none">
                          <i className="fas fa-home"></i>
                        </span>
                        <span className="d-none d-sm-block">Details</span>
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: 'pointer' }}
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => toggleTab('2')}
                      >
                        <span className="d-block d-sm-none">
                          <i className="fas fa-users"></i>
                        </span>
                        <span className="d-none d-sm-block">Tenants</span>
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={activeTab} className="p-3 text-muted">
                    <TabPane tabId="1">
                      <Row>
                        {/* Basic & Additional Information side-by-side */}
                        <Col lg={6}>
                          <Card className="shadow-sm border-0 mb-4 h-100 overflow-hidden">
                            <CardBody>
                              <h5 className="mb-3">
                                <i className="ri-information-line align-middle me-1 text-primary"></i>
                                Basic Information
                              </h5>
                              <table className="table table-sm mb-0">
                                <tbody>
                                  <tr>
                                    <th>Name</th>
                                    <td>{subProperty.name ?? '-'}</td>
                                  </tr>
                                  {subProperty.customFloor && (
                                    <tr>
                                      <th scope="row">Rent Collector</th>
                                      <td>
                                        {subProperty.rentCollectorDetails
                                          ?.name || '-'}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </CardBody>
                          </Card>
                        </Col>

                        {/* Additional Information */}
                        <Col lg={6}>
                          <Card className="shadow-sm border-0 mb-4 h-100 bg-light-subtle overflow-hidden">
                            <CardBody>
                              <h5 className="mb-3">
                                <i className="ri-list-check-2 align-middle me-1 text-primary"></i>
                                Additional Information
                              </h5>
                              <table className="table table-sm mb-0">
                                <tbody>
                                  <tr>
                                    <th>Assessee Number</th>
                                    <td>{subProperty.assesseeNumber ?? '-'}</td>
                                  </tr>
                                  <tr>
                                    <th>Purchase Consideration</th>
                                    <td>
                                      {subProperty.purchaseConsideration ?? '-'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th>Last Renovation</th>
                                    <td>
                                      {subProperty.lastRenovation
                                        ? moment(
                                            subProperty.lastRenovation,
                                          ).format('DD MMM YYYY')
                                        : '-'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th>Case Number</th>
                                    <td>{subProperty.caseNumber ?? '-'}</td>
                                  </tr>
                                  <tr>
                                    <th>Case Reminder</th>
                                    <td>
                                      {subProperty.caseReminder
                                        ? moment(
                                            subProperty.caseReminder,
                                          ).format('DD MMM YYYY')
                                        : '-'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th>Dispute Reminder</th>
                                    <td>
                                      {subProperty.disputeReminder
                                        ? moment(
                                            subProperty.disputeReminder,
                                          ).format('DD MMM YYYY')
                                        : '-'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th>Dispute Reminder Margin (days)</th>
                                    <td>
                                      {subProperty.disputeReminderMargin ?? '-'}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </CardBody>
                          </Card>
                        </Col>

                        {/* Documents */}
                        <Col lg={12}>
                          <div className="mb-4 mt-4">
                            <h5 className="mb-3">Documents & Images</h5>
                            <Row>
                              {docs.map((d, i) => (
                                <Col xs={6} md={3} key={i}>
                                  <Card className="border-0 shadow-sm h-100 text-center hover-shadow overflow-hidden">
                                    <CardBody className="py-4">
                                      <i
                                        className={`${d.icon} fs-32 text-primary mb-2`}
                                      />
                                      <h6 className="fs-13 fw-semibold mb-2">
                                        {d.label}
                                      </h6>
                                      {d.value.length ? (
                                        <>
                                          {d.value.map((l, idx) => (
                                            <Button
                                              key={idx}
                                              size="sm"
                                              color="primary"
                                              className="me-1 mb-1"
                                              onClick={() => handleViewFile(l)}
                                            >
                                              {d.value.length > 1
                                                ? `File ${idx + 1}`
                                                : 'View'}
                                            </Button>
                                          ))}
                                        </>
                                      ) : (
                                        <span className="badge bg-warning-subtle text-warning">
                                          Not uploaded
                                        </span>
                                      )}
                                    </CardBody>
                                  </Card>
                                </Col>
                              ))}

                              <Col lg={12}>
                                <div className="border rounded p-3">
                                  <h6>Property Photos</h6>
                                  <Row>
                                    {subProperty.photos
                                      ?.split(',')
                                      .map((photo, index) => (
                                        <Col
                                          key={index}
                                          lg={3}
                                          md={4}
                                          sm={6}
                                          className="mb-3"
                                        >
                                          <div className="position-relative">
                                            <img
                                              src={getFileViewUrl(photo)}
                                              alt={`Property Photo ${index + 1}`}
                                              className="img-fluid rounded"
                                              style={{
                                                height: '200px',
                                                width: '100%',
                                                objectFit: 'cover',
                                              }}
                                            />
                                            <Button
                                              color="primary"
                                              size="sm"
                                              className="position-absolute top-0 end-0 m-2"
                                              onClick={() =>
                                                handleViewFile(photo)
                                              }
                                            >
                                              <i className="ri-eye-line"></i>
                                            </Button>
                                          </div>
                                        </Col>
                                      ))}
                                  </Row>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>

                        {/* Rent Calculation History */}
                        <Row className="mt-4">
                          <Col lg={12}>
                            <Card className="shadow-sm border-0 bg-light-subtle overflow-hidden">
                              <CardBody>
                                <h5 className="mb-3 d-flex align-items-center">
                                  <i className="ri-calculator-line align-middle me-2 text-primary"></i>
                                  Rent Calculation History
                                </h5>

                                <div className="table-responsive">
                                  <table className="table table-striped table-hover align-middle mb-0">
                                    <thead className="table-light text-center">
                                      <tr>
                                        <th>Rent Amount (₹)</th>
                                        <th>Increment (%)</th>
                                        <th>Duration (Years)</th>
                                        <th>Jantri</th>
                                        <th>Date</th>
                                      </tr>
                                    </thead>
                                    <tbody className="text-center">
                                      {histories.length ? (
                                        histories.map((h, idx) => (
                                          <tr key={idx}>
                                            <td className="fw-semibold">
                                              {h.rentAmount}
                                            </td>
                                            <td>{h.rentIncrementPercentage}</td>
                                            <td>{h.rentIncrementDuration}</td>
                                            <td>
                                              {h.jantri ? (
                                                <span className="badge bg-primary-subtle text-primary">
                                                  {h.jantri}
                                                </span>
                                              ) : (
                                                '-'
                                              )}
                                            </td>
                                            <td>
                                              {moment(h.createdAt).format(
                                                'DD MMM YYYY',
                                              )}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td
                                            colSpan={5}
                                            className="text-center py-3"
                                          >
                                            No history found
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </Row>
                    </TabPane>
                    <TabPane tabId="2">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="mb-0">Tenant Information</h5>
                        <div className="d-flex gap-2">
                          <Button color="primary" onClick={toggleTenantModal}>
                            <i className="ri-add-line align-bottom me-1"></i>
                            {tenant ? 'Edit Tenant' : 'Add Tenant'}
                          </Button>
                          {tenant && (
                            <>
                              <Button
                                color="warning"
                                onClick={() => setDisableConfirm(true)}
                              >
                                InActive Tenant
                              </Button>
                              <Button
                                color="danger"
                                onClick={() => setDeleteTenantModal(true)}
                              >
                                <i className="ri-delete-bin-line align-bottom me-1"></i>
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {tenant ? (
                        <Card className="border-success border-start border-4 mb-4 shadow-sm overflow-hidden">
                          <CardBody>
                            <h6 className="mb-3 text-success">
                              <i className="ri-user-3-line align-middle me-1"></i>
                              Active Tenant
                            </h6>
                            <table className="table table-sm mb-0">
                              <tbody>
                                <tr>
                                  <th style={{ width: '180px' }}>Name</th>
                                  <td>{tenant.tenantName}</td>
                                </tr>
                                <tr>
                                  <th>Contact</th>
                                  <td>{tenant.contactNumber}</td>
                                </tr>
                                <tr>
                                  <th>Email</th>
                                  <td>{tenant.email}</td>
                                </tr>
                                <tr>
                                  <th>GSTIN</th>
                                  <td>{tenant.gstin ?? '-'}</td>
                                </tr>
                                <tr>
                                  <th>PAN</th>
                                  <td>{tenant.pan ?? '-'}</td>
                                </tr>
                                <tr>
                                  <th>Address</th>
                                  <td>{tenant.address ?? '-'}</td>
                                </tr>
                                <tr>
                                  <th>Rent Agreement</th>
                                  <td>
                                    {tenant.rentAgreementDoc
                                      ?.split(',')
                                      .map((doc, index) => (
                                        <Button
                                          key={index}
                                          size="sm"
                                          color="link"
                                          onClick={() => handleViewFile(doc)}
                                        >
                                          View {index + 1}
                                        </Button>
                                      ))}
                                  </td>
                                </tr>
                                <tr>
                                  <th>Photo</th>
                                  <td>
                                    {tenant.photo
                                      ?.split(',')
                                      .map((photo, index) => (
                                        <Button
                                          key={index}
                                          size="sm"
                                          color="link"
                                          onClick={() => handleViewFile(photo)}
                                        >
                                          View {index + 1}
                                        </Button>
                                      ))}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </CardBody>
                        </Card>
                      ) : (
                        <p className="text-muted">No active tenant assigned.</p>
                      )}
                      {disabledTenants.length > 0 && (
                        <div className="mt-4">
                          <Card className="shadow-sm border-0 bg-light-subtle overflow-hidden">
                            <CardBody>
                              <h6 className="mb-3 d-flex align-items-center">
                                <i className="ri-time-line align-middle me-2 text-primary"></i>
                                Tenant History
                              </h6>
                              <div className="table-responsive">
                                <table className="table table-striped table-hover align-middle mb-0">
                                  <thead className="table-light text-center">
                                    <tr>
                                      <th>Name</th>
                                      <th>Disabled By</th>
                                      <th>Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {disabledTenants.map(t => (
                                      <tr key={t.id} className="text-center">
                                        <td>{t.tenantName}</td>
                                        <td>{t.disabledByName ?? '-'}</td>
                                        <td>
                                          {moment(t.disabledAt).format(
                                            'DD MMM YYYY',
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </CardBody>
                          </Card>
                        </div>
                      )}
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <TenantModel
          isOpen={tenantModal}
          toggle={toggleTenantModal}
          propertyId={propertyId || ''}
          subPropertyId={id || ''}
          onSuccess={loadTenantDetails}
          editData={tenant}
        />

        <SubPropertyHistoryModal
          isOpen={historyModal}
          toggle={() => setHistoryModal(false)}
          subPropertyId={id}
          onSuccess={loadHistories}
        />

        {/* Sub Property Modal */}
        <SubPropertyModal
          props={property}
          isOpen={editModal}
          toggle={toggleEditModal}
          parentPropertyId={Number(propertyId)}
          organizationName={mazarName || ''}
          trustRegNumber={trustRegNumber || ''}
          surveyNumber={surveyNumber || ''}
          propertyName={propertyName || ''}
          onSuccess={() => {
            loadSubPropertyDetails();
            loadHistories();
          }}
          editData={subProperty}
        />
      </div>
    </React.Fragment>
  );
};

export default SubProperties;
